import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './ButtonIconContextMenu.css';

const ButtonIconContextMenu = ({
    children,
    menuItems = [{ label: 'Cancella', onClick: () => {} }],
    ariaLabel = 'Menu options'
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const refContainer = useRef(null);
    const refDropdown = useRef(null);
    // The menu is portalled to <body> and placed with fixed coordinates, so a
    // scrolling or overflow-hidden list it sits in neither clips it nor grows
    // a scrollbar to make room for it.
    const [position, setPosition] = useState(null);

    const toggleDropdown = () => {
        setShowDropdown(prev => !prev);
    };

    const handleMenuItemClick = (onClick) => {
        setShowDropdown(false);
        onClick();
    };

    const handleKeyDown = (e) => {
        if (e.keyCode === 13 || e.keyCode === 32) {
            e.preventDefault();
            toggleDropdown();
        }
    };

    useEffect(() => {
        if (!showDropdown) return;

        const handleClickOutside = (e) => {
            if (refContainer.current && !refContainer.current.contains(e.target)
                && !refDropdown.current?.contains(e.target)) {
                setShowDropdown(false);
            }
        };

        const handleEscKey = (e) => {
            if (e.keyCode === 27) {
                e.stopPropagation();
                setShowDropdown(false);
            }
        };

        // Fixed coordinates go stale as soon as anything scrolls.
        // (a resize targets window, which is not a Node)
        const handleScroll = (e) => {
            if (e.target instanceof Node && refDropdown.current?.contains(e.target)) return;
            setShowDropdown(false);
        };

        window.addEventListener('click', handleClickOutside);
        window.addEventListener('keydown', handleEscKey, false);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('click', handleClickOutside);
            window.removeEventListener('keydown', handleEscKey, false);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleScroll);
        };
    }, [showDropdown]);

    // Under the button, right edges aligned; above it when there is no room below.
    useLayoutEffect(() => {
        if (!showDropdown) {
            setPosition(null);
            return;
        }

        const button = refContainer.current?.getBoundingClientRect();
        const menuHeight = refDropdown.current?.offsetHeight ?? 0;
        if (!button) return;

        const gap = 4;
        const fitsBelow = button.bottom + gap + menuHeight <= window.innerHeight;

        setPosition({
            right: Math.max(gap, window.innerWidth - button.right),
            top: fitsBelow ? button.bottom + gap : Math.max(gap, button.top - gap - menuHeight),
        });
    }, [showDropdown]);

    return (
        <div className="buttonIconContextMenu" ref={refContainer}>
            <button
                className="button-icon"
                type="button"
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
                aria-expanded={showDropdown}
                aria-haspopup="true"
                aria-label={ariaLabel}
            >
                {children}
            </button>

            {showDropdown && createPortal(
                <div
                    ref={refDropdown}
                    className="buttonIconContextMenu__dropdown"
                    style={position
                        ? { top: `${position.top}px`, right: `${position.right}px` }
                        : { visibility: 'hidden' }}
                >
                    <ul className="buttonIconContextMenu__menu">
                        {menuItems.map((item, index) => (
                            <li key={index}>
                                <button
                                    className="buttonIconContextMenu__menuItem"
                                    onClick={() => handleMenuItemClick(item.onClick)}
                                    type="button"
                                >
                                    {item.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>,
                // A modal <dialog> sits in the top layer, above anything in <body>.
                refContainer.current?.closest('dialog') || document.body
            )}
        </div>
    );
};

export default ButtonIconContextMenu;
